import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShoppingCart, Search, Filter, Download, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Package, Truck, DollarSign, Clipboard, FileText,
  TrendingUp, Calendar, Users, BarChart2, PieChart, Activity,
  Plus, Minus, X, Edit, Trash2, CheckCircle, AlertTriangle, Loader2,
  Clock, Check, XCircle, RotateCcw, CreditCard
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { posApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  pending: { label: 'در انتظار', variant: 'warning' as const, icon: Clock },
  completed: { label: 'تکمیل شده', variant: 'success' as const, icon: Check },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
  refunded: { label: 'مرجوع', variant: 'info' as const, icon: RotateCcw },
}

export default function SalesHistoryPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<SalesListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.sales(filters),
    queryFn: () => posApi.listSales(filters),
    placeholderData: (prev) => prev,
  })

  const refundMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => posApi.voidSale(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales() })
      toast.success('بازپرداخت ثبت شد')
      closeModal('refund-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'بازپرداخت ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => posApi.voidSale(id, 'حذف توسط کاربر'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sales() })
      toast.success('فروش حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (sale: any) => {
    const items = [
      { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/pos/sales/${sale.id}`, '_blank') },
      { label: 'چاپ رسید', icon: Clipboard, onClick: () => window.open(`/pos/sales/${sale.id}/receipt?format=html`, '_blank') },
      { label: 'خروجی PDF', icon: FileText, onClick: () => { toast.success('در حال ایجاد PDF...'); setTimeout(() => toast('خروجی PDF انجام شد'), 1000) } },
      { type: 'divider' as const },
    ]

    if (sale.status === 'completed' && hasPermission('pos', 'update')) {
      items.push(
        { label: 'بازپرداخت (تمام)', icon: RotateCcw, onClick: () => openModal('refund-confirm') },
        { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(sale.id); openModal('delete-confirm') } }
      )
    }

    return items
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">فروش‌ها</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} فروخت ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-sale')} disabled={!hasPermission('pos', 'create')}>
            <Plus className="w-4 h-4" />
            فروش جدید
          </Button>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {[
          { label: 'کل فروشت', count: data?.items.filter((s: any) => s.status === 'completed').reduce((sum: number, s: any) => sum + s.total_amount, 0) || 0, color: 'primary', icon: DollarSign, format: (c: number) => formatCurrency(c) },
          { label: 'تعداد فروش', count: data?.items.filter((s: any) => s.status === 'completed').length || 0, color: 'success', icon: ShoppingCart, format: (c: number) => formatNumber(c) },
          { label: 'متوسط سفارش', count: data?.items.filter((s: any) => s.status === 'completed').reduce((sum: number, s: any) => sum + s.total_amount, 0) / Math.max(data?.items.filter((s: any) => s.status === 'completed').length, 1), color: 'info', icon: BarChart2, format: (c: number) => formatCurrency(c) },
          { label: 'دریافت شده', count: data?.items.filter((s: any) => s.status === 'completed').reduce((sum: number, s: any) => sum + s.paid_amount, 0) || 0, color: 'warning', icon: CreditCard, format: (c: number) => formatCurrency(c) },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{stat.label}</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white font-mono">{stat.format(stat.count)}</p>
              </div>
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', `bg-${stat.color}-100 dark:bg-${stat.color}-900/30`)}>
                <stat.icon className={cn('w-5 h-5', `text-${stat.color}-600 dark:text-${stat.color}-400`)} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره فروش، نام مشتری)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'pending', label: 'در انتظار' },
              { value: 'completed', label: 'تکمیل شده' },
              { value: 'cancelled', label: 'لغو شده' },
              { value: 'refunded', label: 'مرجوع' },
            ]}
            placeholder="وضعیت"
            className="w-36"
          />
          <Select
            value={filters.shift_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, shift_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه شیفت‌ها' }]}
            placeholder="شیفت"
            className="w-36"
          />
          <Select
            value={filters.warehouse_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, warehouse_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه انبارها' }]}
            placeholder="انبار"
            className="w-36"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی CSV', icon: Download, onClick: () => {} },
              { label: 'خروجی Excel', icon: FileText, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره فروش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">مبلغ کل</th>
                    <th className="hidden lg:table-cell">پرداخت شده</th>
                    <th>مانده</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری فروش‌ها</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingCart className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ فروخی یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین فروش را ایجاد کنید</p>
            <Button onClick={() => openModal('create-sale')} disabled={!hasPermission('pos', 'create')}>
              <Plus className="w-4 h-4 ml-1" />
              فروش جدید
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره فروش</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">مبلغ کل</th>
                    <th className="hidden lg:table-cell">پرداخت شده</th>
                    <th>مانده</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((sale, index) => {
                    const statusInfo = statusConfig[sale.status as keyof typeof statusConfig] || statusConfig.pending
                    const StatusIcon = statusInfo.icon
                    const balance = sale.total_amount - sale.paid_amount
                    return (
                      <motion.tr
                        key={sale.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={sale.status === 'refunded' ? 'bg-warning-50/50 dark:bg-warning-900/20' : sale.status === 'cancelled' ? 'bg-danger-50/50 dark:bg-danger-900/20' : ''}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{sale.sale_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(sale.created_at)}</td>
                        <td className="hidden md:table-cell font-mono text-sm text-neutral-900 dark:text-white">{formatCurrency(sale.total_amount)}</td>
                        <td className="hidden lg:table-cell font-mono text-sm text-success-600 dark:text-success-400">{formatCurrency(sale.paid_amount)}</td>
                        <td className="font-mono text-sm {balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'}">{formatCurrency(balance)}</td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(sale)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Refund Confirm Modal */}
      <Modal
        isOpen={modals['refund-confirm']}
        onClose={() => closeModal('refund-confirm')}
        title="بازپرداخت فروش"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-neutral-600 dark:text-neutral-400">
            دلیل بازپرداخت را وارد کنید:
          </p>
          <Input placeholder="مثال: کالای معیوب، اختلاف قیمت..." />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => closeModal('refund-confirm')}>انصراف</Button>
          <Button variant="warning" onClick={() => {}}>ثبت بازپرداخت</Button>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف فروش"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این فروش مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const salesStatsCards = [
  { label: 'کل فروش', icon: DollarSign, color: 'primary' },
  { label: 'تعداد فروش', icon: ShoppingCart, color: 'success' },
  { label: 'متوسط سفارش', icon: BarChart2, color: 'info' },
  { label: 'دریافت شده', icon: CreditCard, color: 'warning' },
]

export function SalesStatsCards({ sales }: { sales: any[] }) {
  const completedSales = sales.filter(s => s.status === 'completed')
  const totalSales = completedSales.reduce((sum: number, s: any) => sum + s.total_amount, 0)
  const paidSales = completedSales.reduce((sum: number, s: any) => sum + s.paid_amount, 0)
  const avgOrder = completedSales.length > 0 ? totalSales / completedSales.length : 0

  return salesStatsCards.map((stat, i) => {
    const value = stat.label === 'کل فروش' ? totalSales : stat.label === 'تعداد فروش' ? completedSales.length : stat.label === 'متوسط سفارش' ? avgOrder : paidSales
    const formatValue = (val: number) => {
      switch (stat.label) {
        case 'کل فروش': return formatCurrency(val)
        case 'تعداد فروش': return formatNumber(val)
        case 'متوسط سفارش': return formatCurrency(val)
        case 'دریافت شده': return formatCurrency(val)
        default: return val.toString()
      }
    }
    return (
      <div key={stat.label} className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{stat.label}</p>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white font-mono">{formatValue(value)}</p>
          </div>
          <div className={`w-10 h-10 rounded-xl bg-${stat.color}-100 dark:bg-${stat.color}-900/30 flex items-center justify-center` }>
            <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400` } />
          </div>
        </div>
      </div>
    )
  })
}

type SalesListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  shift_id?: string
  warehouse_id?: string
}